import React from 'react';
import { Box, styled, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { useLocation, Link } from 'react-router-dom';

// Styled Components
const PageContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: 'white',
  paddingTop: '140px',
  paddingBottom: '80px',
});

const ContentWrapper = styled(Box)({
  maxWidth: '900px',
  margin: '0 auto',
  padding: '0 40px',
  
  '@media (max-width: 768px)': {
    padding: '0 20px',
  },
});

const BackButton = styled(Link)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'Raleway',
  fontSize: '14px',
  letterSpacing: '1px',
  color: '#666',
  textDecoration: 'none',
  marginBottom: '40px',
  transition: 'color 0.3s ease',
  
  '&:hover': {
    color: '#000',
  },
});

const PageTitle = styled('h1')({
  fontFamily: 'Oswald',
  fontSize: '42px',
  fontWeight: 400,
  letterSpacing: '1px',
  marginBottom: '16px',
  marginTop: 0,
  lineHeight: '1.2',
  
  '@media (max-width: 768px)': {
    fontSize: '32px',
  },
});

const MetaInfo = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '24px',
  marginBottom: '32px',
  paddingBottom: '32px',
  borderBottom: '1px solid #E0E0E0',
});

const MetaItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'Raleway',
  fontSize: '15px',
  color: '#666',
});

const MetaLabel = styled('span')({
  fontFamily: 'Raleway',
  fontWeight: 600,
  color: '#000',
});

const HeroImage = styled('img')({
  width: '100%',
  height: 'auto',
  maxHeight: '500px',
  objectFit: 'cover',
  marginBottom: '40px',
  borderRadius: '4px',
});

const Description = styled(Typography)({
  fontFamily: 'Raleway',
  fontSize: '18px',
  lineHeight: '1.8',
  color: '#333',
  marginBottom: '40px',
});

const ContentSection = styled(Box)({
  fontFamily: 'Raleway',
  fontSize: '16px',
  lineHeight: '1.8',
  color: '#333',
  
  '& h4': {
    fontFamily: 'Oswald',
    fontSize: '24px',
    fontWeight: 500,
    letterSpacing: '1px',
    marginTop: '32px',
    marginBottom: '16px',
  },
  
  '& ul': {
    paddingLeft: '24px',
    marginBottom: '24px',
  },
  
  '& li': {
    marginBottom: '12px',
    lineHeight: '1.6',
  },
});

const SocialLinks = styled(Box)({
  display: 'flex',
  gap: '16px',
  marginTop: '40px',
  paddingTop: '40px',
  borderTop: '1px solid #E0E0E0',
  flexWrap: 'wrap',
});

const SocialLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'Oswald',
  fontSize: '14px',
  letterSpacing: '1px',
  color: '#000',
  textDecoration: 'none',
  padding: '10px 20px',
  border: '1px solid #000',
  borderRadius: '4px',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: '#000',
    color: '#fff',
  },
});

// Component
const CommunityDetail = () => {
  const location = useLocation();
  const { activity } = location.state || {};

  if (!activity) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography style={{ fontFamily: 'Raleway', fontSize: '24px', marginBottom: '20px' }}>
            Ooopssss....How did you get here?
          </Typography>
          <BackButton to="/community">
            <Icon icon="mdi:arrow-left" style={{ fontSize: '20px' }} />
            Back 
          </BackButton>
        </ContentWrapper>
      </PageContainer>
    );
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <BackButton to="/community">
          <Icon icon="mdi:arrow-left" style={{ fontSize: '20px' }} />
          Back 
        </BackButton>

        <PageTitle>{activity.name}</PageTitle>

        <MetaInfo>
          <MetaItem>
            <Icon icon="mdi:office-building-outline" style={{ fontSize: '20px' }} />
            <MetaLabel>Community:</MetaLabel> {activity.organization}
          </MetaItem>
          
          {activity.role && (
            <MetaItem>
              <Icon icon="mdi:account-outline" style={{ fontSize: '20px' }} />
              <MetaLabel>Role:</MetaLabel> {activity.role}
            </MetaItem>
          )}
          
          <MetaItem>
            <Icon icon="mdi:calendar-outline" style={{ fontSize: '20px' }} />
            <MetaLabel>Period:</MetaLabel> {activity.datePeriod}
          </MetaItem>
        </MetaInfo>

        {activity.image && (
          <HeroImage src={activity.image} alt={activity.name} />
        )}

        {activity.description && (
          <Description>{activity.description}</Description>
        )}

        {activity.content && (
          <ContentSection dangerouslySetInnerHTML={{ __html: activity.content }} />
        )}

        {activity.cert && (
          <Box sx={{ marginTop: '32px', textAlign: 'center' }}>
            <a 
              href={activity.cert} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <Button
                style={{
                  fontFamily: 'Oswald',
                  fontSize: '14px',
                  letterSpacing: '1px',
                  padding: '12px 32px',
                  border: '2px solid #000',
                  backgroundColor: '#000',
                  color: '#fff',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#fff';
                  e.target.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#000';
                  e.target.style.color = '#fff';
                }}
              >
                <Icon icon="mdi:certificate-outline" style={{ fontSize: '20px', marginRight: '8px' }} />
                VIEW CERTIFICATE
              </Button>
            </a>
          </Box>
        )}

        {(activity.twitter || activity.instagram || activity.linkedin || activity.website) && (
          <SocialLinks>
            {activity.twitter && (
              <SocialLink 
                href={activity.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Icon icon="mdi:twitter" style={{ fontSize: '18px' }} />
                Twitter
              </SocialLink>
            )}
            {activity.instagram && (
              <SocialLink 
                href={activity.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Icon icon="mdi:instagram" style={{ fontSize: '18px' }} />
                Instagram
              </SocialLink>
            )}
            {activity.linkedin && (
              <SocialLink 
                href={activity.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Icon icon="mdi:linkedin" style={{ fontSize: '18px' }} />
                LinkedIn
              </SocialLink>
            )}
            {activity.website && (
              <SocialLink 
                href={activity.website} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Icon icon="mdi:web" style={{ fontSize: '18px' }} />
                Website
              </SocialLink>
            )}
          </SocialLinks>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default CommunityDetail;